<template>
  <Teleport to="body">
    <!-- Backdrop: full-viewport, click-through, sits one z-index below
         the panel. Clicking it triggers "show later" — the gentle
         "not finished, not permanently dismissed" exit. -->
    <div
      v-if="isVisible"
      class="tour-backdrop"
      @click="showLater"
      aria-hidden="true"
    />
    <section
      v-if="isVisible && currentStep"
      ref="panelEl"
      class="tour-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-tour-title"
      aria-describedby="onboarding-tour-desc"
    >
      <header class="tour-head">
        <div>
          <p class="tour-eyebrow">Onboarding</p>
          <h3 id="onboarding-tour-title" class="tour-title">{{ currentStep.label }}</h3>
        </div>
        <span class="tour-counter mono">{{ currentStepIndex + 1 }} / {{ TOUR_STEPS.length }}</span>
      </header>

      <p id="onboarding-tour-desc" class="tour-desc">{{ currentStep.description }}</p>

      <p v-if="currentStep.callout" class="tour-callout">
        <span class="tour-callout-label mono">Tip</span>
        <span>{{ currentStep.callout }}</span>
      </p>

      <div class="tour-progress-track">
        <div
          class="tour-progress-fill"
          role="progressbar"
          :aria-valuenow="progressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
          :style="{ width: progressPercent + '%' }"
        />
      </div>

      <div class="tour-foot">
        <div class="tour-foot-left">
          <button type="button" class="btn-ghost btn-sm" @click="showLater">Show later</button>
          <button type="button" class="btn-ghost btn-sm" @click="dismissForever">Close</button>
        </div>
        <div class="tour-foot-right">
          <button type="button" class="btn-secondary btn-sm" :disabled="isFirstStep" @click="goBack">Back</button>
          <button type="button" class="btn-primary btn-sm" @click="goNext">
            {{ isLastStep ? 'Finish onboarding' : 'Next' }}
          </button>
        </div>
      </div>
    </section>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth.js'
import { trapFocus, releaseFocus } from '@/lib/focusTrap.js'
import {
  dismissFirstTour,
  TOUR_UPDATED_EVENT,
  getActiveFirstTour,
  getPendingFirstTour,
  hasCompletedFirstTour,
  hasDismissedFirstTour,
  markFirstTourCompleted,
  postponeFirstTour,
} from '@/utils/firstTourState.js'

// The 7-step walkthrough. Hard-coded here (the file is the right
// place — Prufa's sidebar is static, not a dynamic nav map).
const TOUR_STEPS = [
  {
    matchPath: (p) => p === '/',
    label: 'Workspace',
    description: "Quota and alert channels (Slack, email, and your own usage webhook) for your workspace. You'll see a hard-cap warning here the moment monitoring pauses — never a dead end.",
  },
  {
    matchPath: (p) => p === '/new',
    label: 'Start a test',
    description: 'Pick how deep to go. A quick audit is a one-URL smoke test for a shareable report in under a minute; flows are the full user-journey coverage the agent drives in a real browser.',
    callout: 'Try your own production URL.',
  },
  {
    matchPath: (p) => p.startsWith('/runs'),
    label: 'Runs',
    description: "Every audit and monitor run lands here, with the same evidence a human QA engineer would attach. Click any run for the full report.",
  },
  {
    matchPath: (p) => p.startsWith('/monitors'),
    label: 'Monitors',
    description: 'Continuous monitoring with Slack, email, or your own signed usage webhook. Set a flow, pick a cadence, get notified on whichever channel is on.',
  },
  {
    matchPath: (p) => p.startsWith('/flows'),
    label: 'Flows',
    description: 'Plain-text test cases. Full-auto discovery is gated to paid tiers — your first flow is hand-written and runs forever.',
  },
  {
    matchPath: (p) => p === '/api-keys',
    label: 'API keys',
    description: 'The agent surface entry point. Create a key and Prufa becomes callable from your CI, your coding agent, or your Slack bot.',
    callout: 'Full reference at /api/v1/docs.',
  },
  {
    matchPath: (p) => p === '/mcp',
    label: 'MCP server',
    description: 'Wire Prufa into Claude Code, Cursor, or Cline over MCP. One install, one config block — the free audit needs no key, and your saved workspace, flows, and monitors unlock with the key from the last step.',
    callout: 'Open source: github.com/prufa-dev/prufa-mcp.',
  },
  {
    matchPath: () => true, // last step — always matches
    label: "You're set",
    description: "That's the dashboard. Prufa is also a CLI and a skill file — visit /skill/SKILL.md to drop the full agent onboarding into Claude Code, Cursor, or Cline.",
  },
]

const route = useRoute()
const router = useRouter()
const { user } = useAuth()

const isVisible = ref(false)
const currentStepIndex = ref(0)
const panelEl = ref(null)
// Suppresses syncFromRoute during a programmatic navigation we initiated
// (goNext/goBack). Without this, pushing to the next step's route fires
// the route watcher, which resets currentStepIndex to the first step
// whose matchPath returns true — and the user gets bounced back to
// step 1 when they reach the last step.
const userNavigating = ref(false)

const workspaceId = computed(() => user.value?.workspace_id || null)
const currentStep = computed(() => TOUR_STEPS[currentStepIndex.value] ?? null)
const isFirstStep = computed(() => currentStepIndex.value === 0)
const isLastStep = computed(() => currentStepIndex.value >= TOUR_STEPS.length - 1)
const progressPercent = computed(() => {
  if (TOUR_STEPS.length <= 1) return 100
  return Math.round(((currentStepIndex.value + 1) / TOUR_STEPS.length) * 100)
})

function syncFromRoute() {
  if (userNavigating.value) return
  const idx = TOUR_STEPS.findIndex((step) => step.matchPath(route.path))
  if (idx >= 0) currentStepIndex.value = idx
}

function syncVisibility() {
  const wid = workspaceId.value
  if (!wid) {
    isVisible.value = false
    return
  }
  if (getActiveFirstTour(wid) === '1') {
    isVisible.value = true
    syncFromRoute()
    return
  }
  if (getPendingFirstTour(wid) === '1') {
    isVisible.value = true
    syncFromRoute()
    return
  }
  isVisible.value = false
}

function finishTour() {
  const wid = workspaceId.value
  if (!wid) return
  markFirstTourCompleted(wid)
  isVisible.value = false
}

function showLater() {
  const wid = workspaceId.value
  if (!wid) return
  postponeFirstTour(wid)
  isVisible.value = false
}

function dismissForever() {
  const wid = workspaceId.value
  if (!wid) return
  dismissFirstTour(wid)
  isVisible.value = false
}

async function goBack() {
  if (isFirstStep.value) return
  const prev = TOUR_STEPS[currentStepIndex.value - 1]
  if (!prev) return
  // The matcher takes the route, so we just bump the index and route
  // will follow from syncFromRoute on the next watch tick.
  currentStepIndex.value -= 1
  // Navigate to the step's natural route if we can infer it.
  const targetRoute = stepToRoute(currentStepIndex.value)
  if (targetRoute) {
    userNavigating.value = true
    try {
      await router.push(targetRoute)
    } finally {
      // Release the guard on the next tick so the route change has
      // already been processed by Vue Router before syncFromRoute runs.
      await nextTick()
      userNavigating.value = false
    }
  }
}

async function goNext() {
  if (isLastStep.value) {
    finishTour()
    return
  }
  currentStepIndex.value += 1
  const targetRoute = stepToRoute(currentStepIndex.value)
  if (targetRoute) {
    userNavigating.value = true
    try {
      await router.push(targetRoute)
    } finally {
      await nextTick()
      userNavigating.value = false
    }
  }
}

// Minimal reverse-lookup: which route should each step be on?
function stepToRoute(idx) {
  switch (idx) {
    case 0: return '/'
    case 1: return '/new'
    case 2: return '/runs'
    case 3: return '/monitors'
    case 4: return '/flows'
    case 5: return '/api-keys'
    case 6: return '/mcp'
    case 7: return '/' // last step lands on Workspace
    default: return null
  }
}

function onKeyDown(e) {
  if (!isVisible.value) return
  if (e.key === 'Escape') {
    e.preventDefault()
    showLater()
  }
}

function onTourStateUpdated() {
  syncVisibility()
  if (isVisible.value && !userNavigating.value) syncFromRoute()
}

watch(isVisible, async (v) => {
  if (v) {
    await nextTick()
    if (panelEl.value) trapFocus(panelEl.value)
  } else {
    releaseFocus()
  }
})

watch(
  () => [route.path, workspaceId.value],
  () => {
    syncVisibility()
    if (isVisible.value && !userNavigating.value) syncFromRoute()
  },
  { immediate: true }
)

onMounted(() => {
  window.addEventListener(TOUR_UPDATED_EVENT, onTourStateUpdated)
  window.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener(TOUR_UPDATED_EVENT, onTourStateUpdated)
  window.removeEventListener('keydown', onKeyDown)
  releaseFocus()
})

// Exposed for test/devtools introspection (no behavior change).
defineExpose({ TOUR_STEPS })
</script>

<style scoped>
.tour-backdrop {
  position: fixed;
  inset: 0;
  z-index: 39;
  background: transparent;
}

.tour-panel {
  position: fixed;
  top: 96px;
  right: 24px;
  z-index: 40;
  width: min(26rem, calc(100vw - 32px));
  min-height: 40vh;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
  font-family: "Satoshi", system-ui, sans-serif;
  color: var(--ink);
  box-shadow: 0 8px 24px rgba(24, 24, 27, 0.08);
}

@media (max-width: 640px) {
  .tour-panel {
    top: 80px;
    right: 16px;
    left: 16px;
    width: auto;
  }
}

.tour-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.tour-eyebrow {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--green);
  margin-bottom: 4px;
}
.tour-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}
.tour-counter {
  font-size: 11px;
  color: var(--muted);
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
}

.tour-desc {
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ink);
}

.tour-callout {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 12px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--ink);
}
.tour-callout-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--slate);
  flex-shrink: 0;
}

.tour-progress-track {
  margin-top: auto;
  padding-top: 16px;
  height: 6px;
  background: var(--bg);
  border-radius: 999px;
  overflow: hidden;
}
.tour-progress-fill {
  height: 100%;
  background: var(--green);
  transition: width 200ms ease;
}

.tour-foot {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.tour-foot-left,
.tour-foot-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
